import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AppHeader from './AppHeader';
import { useBranding } from '../Context/BrandingContext';
import { useLoginModal } from '../Context/LoginModalContext';
import { getSessionToken } from '../Services/services';
import { getCatalogOfferings, getRepairCapabilityItem, addRepairToCart } from '../api/inventory';
import { track, logger } from '../utils/logger';
import { useApprovedAccess } from '../utils/useApprovedAccess';
import RestrictedAccess from './RestrictedAccess';
import { fmtPrice } from './InventoryDetailsModal';

const SERVICES = [
  { key: 'inspection', label: 'Inspection', mro_service: 'inspection', amountKey: 'inspection_amount' },
  { key: 'repair', label: 'Repair', mro_service: 'repair', amountKey: 'repair_amount' },
  { key: 'overhaul', label: 'Overhaul', mro_service: 'overhaul', amountKey: 'overhaul_amount' },
];

export default function RepairCatalogDetail() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const { branding } = useBranding();
  const { openLogin } = useLoginModal();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [item, setItem] = useState(null);
  const [adding, setAdding] = useState({});
  const [toast, setToast] = useState(null);
  const [pricingDisplayed, setPricingDisplayed] = useState(false);

  const isAuthenticated = !!getSessionToken();
  // Detail view is approved-only; guests/pending can search + view results only.
  const { checked: accessChecked, allowed: accessAllowed, isGuest } = useApprovedAccess();

  useEffect(() => {
    if (!accessAllowed) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    getRepairCapabilityItem(decodeURIComponent(itemId))
      .then((data) => { if (!cancelled) setItem(data); })
      .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to load capability'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [itemId, accessAllowed]);

  useEffect(() => {
    let cancelled = false;
    getCatalogOfferings()
      .then((data) => { if (!cancelled) setPricingDisplayed(data?.repair_pricing_displayed === true); })
      .catch(() => { if (!cancelled) setPricingDisplayed(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const handleAddToCart = async (svc) => {
    if (!isAuthenticated) {
      openLogin({ redirectPath: window.location.pathname });
      return;
    }
    setAdding((prev) => ({ ...prev, [svc.key]: true }));
    try {
      await addRepairToCart({
        catalog_kind: 'repair',
        part_number: item.part_number,
        sf_product_id: item.sf_product_id || undefined,
        sf_repair_capability_id: item.sf_repair_capability_id || undefined,
        description: item.keyword || item.part_number,
        mro_service: svc.mro_service,
      });
      window.dispatchEvent(new Event('cartUpdated'));
      track('cart.add', { kind: 'repair', service: svc.mro_service });
      setToast({ type: 'success', message: `${svc.label} service added to cart` });
    } catch (e) {
      logger.error('cart.add_failed', { kind: 'repair', service: svc.mro_service, status: e?.status, message: e?.message });
      setToast({ type: 'error', message: e?.message || 'Failed to add to cart' });
    } finally {
      setAdding((prev) => ({ ...prev, [svc.key]: false }));
    }
  };

  const offeredServices = item
    ? SERVICES.filter((s) => item[s.key] === true)
    : [];
  const quotableServices = offeredServices.filter(
    (s) => !(pricingDisplayed && item?.[s.amountKey] != null)
  );

  if (accessChecked && !accessAllowed) {
    return <RestrictedAccess isGuest={isGuest} redirectPath={window.location.pathname} />;
  }

  return (
    <div style={styles.page}>
      <AppHeader />

      {toast && (
        <div style={{
          ...styles.toast,
          backgroundColor: toast.type === 'success' ? '#059669' : '#dc2626',
        }}>
          {toast.message}
        </div>
      )}

      <main style={styles.main}>
        <div style={styles.container}>
          {loading && <p style={styles.muted}>Loading…</p>}
          {error && <p style={styles.error}>{error}</p>}
          {!loading && !error && item && (
            <div style={styles.card}>
              <h1 style={styles.partNumber}>{item.part_number}</h1>
              {item.keyword && <p style={styles.keyword}>{item.keyword}</p>}

              <div style={styles.metaGrid}>
                <div>
                  <span style={styles.metaLabel}>Part Number</span>
                  <span style={styles.metaValue}>{item.part_number}</span>
                </div>
                {item.ata_chapter && (
                  <div>
                    <span style={styles.metaLabel}>ATA Chapter</span>
                    <span style={styles.metaValue}>{item.ata_chapter}</span>
                  </div>
                )}
                {item.sf_product_id && (
                  <div>
                    <span style={styles.metaLabel}>Product ID</span>
                    <span style={styles.metaValue}>{item.sf_product_id}</span>
                  </div>
                )}
              </div>

              <h2 style={styles.servicesTitle}>Available Services</h2>
              {offeredServices.length === 0 ? (
                <p style={styles.muted}>No services currently offered for this part.</p>
              ) : (
                <div style={styles.serviceGrid}>
                  {offeredServices.map((svc) => {
                    // A priced service already gives the customer the number, so it is
                    // shown for reference and the quote request is withdrawn.
                    const amount = item[svc.amountKey];
                    const priced = pricingDisplayed && amount != null;
                    return (
                      <div key={svc.key} style={styles.serviceCard}>
                        <div style={styles.serviceName}>{svc.label}</div>
                        <div style={styles.serviceDesc}>
                          {priced && 'Price shown below.'}
                          {!priced && svc.key === 'inspection' && 'Request an inspection quote for this part.'}
                          {!priced && svc.key === 'repair' && 'Request a repair quote for this part.'}
                          {!priced && svc.key === 'overhaul' && 'Request an overhaul quote for this part.'}
                        </div>
                        {priced ? (
                          <div style={styles.servicePrice}>{fmtPrice(amount)}</div>
                        ) : (
                          <button
                            type="button"
                            style={{ ...styles.addBtn, backgroundColor: branding.primaryColor }}
                            onClick={() => handleAddToCart(svc)}
                            disabled={!!adding[svc.key]}
                          >
                            {adding[svc.key] ? 'Adding…' : 'Request Quote'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {!isAuthenticated && quotableServices.length > 0 && (
                <p style={styles.loginNote}>
                  <button
                    type="button"
                    style={styles.loginLink}
                    onClick={() => openLogin({ redirectPath: window.location.pathname })}
                  >
                    Sign in
                  </button>{' '}
                  to request a repair quote.
                </p>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', backgroundColor: 'var(--background)', color: 'var(--foreground)' },
  toast: {
    position: 'fixed', top: 20, right: 20, zIndex: 9999,
    padding: '12px 20px', borderRadius: 8, color: 'white', fontWeight: 600,
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
  },
  main: { padding: '28px 24px' },
  container: { maxWidth: 900, margin: '0 auto' },
  card: {
    backgroundColor: 'var(--card)', border: '1px solid var(--border)',
    borderRadius: 12, padding: 28, boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  },
  partNumber: { margin: 0, fontSize: 28, fontWeight: 800 },
  keyword: { margin: '6px 0 0', color: 'var(--muted-foreground)', fontSize: 16 },
  metaGrid: { display: 'flex', gap: 32, marginTop: 20, flexWrap: 'wrap' },
  metaLabel: { display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--muted-foreground)', letterSpacing: '0.06em', textTransform: 'uppercase' },
  metaValue: { display: 'block', fontSize: 15, fontWeight: 600, marginTop: 2 },
  servicesTitle: { margin: '28px 0 14px', fontSize: 18, fontWeight: 700 },
  serviceGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 },
  serviceCard: {
    border: '1px solid var(--border)', borderRadius: 10,
    padding: 20, display: 'flex', flexDirection: 'column', gap: 8,
    backgroundColor: 'var(--secondary)',
  },
  serviceName: { fontWeight: 700, fontSize: 17 },
  serviceDesc: { fontSize: 13, color: 'var(--muted-foreground)', flex: 1 },
  servicePrice: { marginTop: 8, fontSize: 20, fontWeight: 800, fontVariantNumeric: 'tabular-nums' },
  addBtn: {
    marginTop: 8, border: 'none', borderRadius: 8, padding: '10px 14px',
    color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: 14,
  },
  muted: { color: 'var(--muted-foreground)', marginTop: 16 },
  error: { color: 'var(--destructive)', marginTop: 16 },
  loginNote: { marginTop: 24, color: 'var(--muted-foreground)' },
  loginLink: {
    background: 'none', border: 'none', padding: 0,
    cursor: 'pointer', color: 'inherit', fontWeight: 700, textDecoration: 'underline',
  },
};
